/****************
 * THE ORB SPEAKING AT THE MOMENT SOMEONE GETS STUCK. 2026-09-29.
 *
 * Guillaume: "I thought it was also supposed to analyse what a user is doing
 * live so it could help guide the user when he feels it might need some help,
 * like someone who did not create a strategy but is trying to launch a
 * session, the Orb should manifest and send him to the session creation."
 *
 * Until now the Orb only ever spoke from the once-a-day check on the server
 * (assistant_nudge.go): the app asked every ten minutes whether there was a
 * message, and there never was one for somebody standing in front of an empty
 * strategy list. David Tshimanga spent 38 minutes on the copy-trade screen on
 * 2026-09-23 and the Orb said nothing.
 *
 * A screen that sees the moment raises a live message here; CopyFiOrb shows
 * it at once -- the ding, the red 1, a bubble saying what it is about, and for
 * a click that went nowhere the chat opens by itself -- with the guide that
 * walks them to the control they need.
 *
 * ── In the app only ────────────────────────────────────────────────────────
 *
 * Nothing here reaches the server except the analytics event saying it was
 * shown. A live message is never stored as a nudge row, so it can never
 * become an email.
 *
 * ── Once, not every time ───────────────────────────────────────────────────
 *
 * A message the person did not ask for, repeated, is nagging. Each trigger
 * speaks at most once a day on this browser -- unless the person asked for it
 * by pressing something that did nothing (`force`), which is a question and
 * always gets an answer.
 ****************/

import { isOnboardingActive } from '@/lib/onboarding';

export const ORB_LIVE_EVENT = 'copyfi:orblive:raise';

/****************
 * "WHY?" (2026-10-21, engagement redesign): open the Orb and ask it a question
 * the screen wrote from what the person is looking at (a loss, a metric, a
 * trader's pick). The Orb explains; it never suggests a setting.
 ****************/
export const ORB_ASK_EVENT = 'copyfi:orb:ask';
/** guide: a Show me (orbGuides.ts) to offer under the answer when the model names none,
 *  the obvious path for the thing asked about (Learn's articles, 2026-10-24). */
export function askOrb(question: string, guide?: string): void {
  window.dispatchEvent(new CustomEvent<{ question: string; guide?: string }>(ORB_ASK_EVENT, { detail: { question, guide } }));
}

export interface LiveNudge {
    /** Which moment this is: the cooldown key and the analytics value. */
    trigger: string;
    /** learn.json keys: the chat message, and the short bubble beside the Orb. */
    messageKey: string;
    bubbleKey: string;
    /** A guide id from orbGuides.ts, offered as the "Show me" button. */
    guide?: string;
    /** Open the chat now rather than waiting for the Orb to be clicked. */
    open?: boolean;
    /** The person pressed something that did nothing: answer even inside the cooldown. */
    force?: boolean;
    /** Values for the message and bubble texts ({{pct}}, {{amount}}...), 2026-10-22. */
    values?: Record<string, string | number>;
}

export const LIVE_COOLDOWN_MS = 24 * 60 * 60 * 1000;
const KEY = (trigger: string) => `copyfi-orb-live:${trigger}`;

function spokeRecently(trigger: string, now: number): boolean {
    try {
        const at = Number(localStorage.getItem(KEY(trigger)) || 0);
        return at > 0 && now - at < LIVE_COOLDOWN_MS;
    } catch {
        return false;
    }
}

function remember(trigger: string, now: number) {
    try { localStorage.setItem(KEY(trigger), String(now)); } catch { /* shown anyway */ }
}

/** Raise a live message. Returns whether it was raised (false = inside its cooldown). */
export function raiseLiveNudge(n: LiveNudge, now: number = Date.now()): boolean {
    // The welcome chooser, its video or a welcome guide is on screen: they are
    // already being walked through it. Not remembered, so it can speak later.
    if (isOnboardingActive()) return false;
    if (!n.force && spokeRecently(n.trigger, now)) return false;
    remember(n.trigger, now);
    window.dispatchEvent(new CustomEvent<LiveNudge>(ORB_LIVE_EVENT, { detail: n }));
    return true;
}

/****************
 * WHAT IS STILL MISSING BEFORE A SESSION CAN START, IN THE ORDER THE SCREEN ASKS.
 *
 * The start button stays grey until trader, strategy and destination are all
 * chosen, and grey says nothing about which one. This names the first missing
 * one, and for a strategy or a destination it tells apart "you have not picked
 * one" from "you do not have one yet" -- those are different walls with
 * different ways over them.
 *
 * Pure, so a test drives it without a screen.
 ****************/
export interface StartState {
    traderChosen: boolean;
    strategyChosen: boolean;
    strategiesSaved: number;
    destinationChosen: boolean;
    exchangesConnected: number;
}

export function missingStep(s: StartState): Omit<LiveNudge, 'open' | 'force'> | null {
    if (!s.traderChosen) {
        return { trigger: 'start-no-trader', messageKey: 'orb.live.noTrader.message', bubbleKey: 'orb.live.noTrader.bubble', guide: 'find-a-trader' };
    }
    if (!s.strategyChosen) {
        return s.strategiesSaved === 0
            ? { trigger: 'start-no-strategy', messageKey: 'orb.live.noStrategy.message', bubbleKey: 'orb.live.noStrategy.bubble', guide: 'create-strategy' }
            : { trigger: 'start-pick-strategy', messageKey: 'orb.live.pickStrategy.message', bubbleKey: 'orb.live.pickStrategy.bubble', guide: 'pick-strategy' };
    }
    if (!s.destinationChosen) {
        return s.exchangesConnected === 0
            ? { trigger: 'start-no-exchange', messageKey: 'orb.live.noExchange.message', bubbleKey: 'orb.live.noExchange.bubble', guide: 'connect-exchange' }
            // No guide: the only one that points at the destination box
            // (try-without-money) tells the person to choose Paper, and
            // somebody with an exchange connected may well mean to go live.
            : { trigger: 'start-pick-destination', messageKey: 'orb.live.pickDestination.message', bubbleKey: 'orb.live.pickDestination.bubble' };
    }
    return null;
}
